import React, { useState } from 'react';

import {
  Button,
  Flexbox,
  FontAwesomeIcon,
  Text,
  Toggle,
  Tooltip,
  WithPopover,
} from '@schoolinks/ui-components';
import cn from 'classnames';

import { bool, func, number, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faShare } from '@fortawesome/pro-regular-svg-icons/faShare';

import RadioGroup from '@mui/material/RadioGroup';

import Radio from 'components/Common/Radio';

import Api from 'core/api';
import useFetch from 'hooks/useFetch';
import { ensureObject } from 'utils/objects';

import s from './ShowPageHeader.css';

const ShowPageHeader = ({
  credentialName,
  isPublic,
  setIsPublic,
  setCredentialVisibility,
  type,
  credentialId,
  copyToClipboard,
  exportCredential,
  shareCredential,
  hideActions,
  credentialCredentialType,
}) => {
  const { t } = useTranslation();
  const [selectedExportFormat, setSelectedExportFormat] = useState('3.0');

  const { data: profile } = useFetch({
    apiFunc: Api.studentProfile.getStudentProfile,
    apiFuncName: 'studentProfile.getStudentProfile',
    options: {
      enabled: !isPublic,
    },
  });
  const { isCredentialWalletPublic } = ensureObject(profile);

  const { pathname } = useLocation();
  // Remove the type and credentialId from the pathname
  const credentialsListUrl = pathname.split('/').slice(0, -2).join('/');

  const getTooltip = () => {
    if (!isCredentialWalletPublic) {
      return t('CredentialWallet.ShowPageHeader.CannotSetCredentialAsPublic');
    }

    return isPublic
      ? t('CredentialWallet.ShowPageHeader.CopyPublicLink')
      : t('CredentialWallet.ShowPageHeader.CannotCopyLinkToPrivateCr');
  };

  const handleExport = () => {
    exportCredential(selectedExportFormat);
  };

  const renderExportPopover = ({ setAnchor }) => (
    <Flexbox flexDirection="column" gap={0.5}>
      <Text variant="bodyReg500">
        {t('CredentialWallet.ShowPageHeader.SelectExportFormat')}
      </Text>

      <Text variant="captionReg400">
        {t('CredentialWallet.ShowPageHeader.SelectExportFormatDescription')}
      </Text>

      <RadioGroup
        classes={{ root: s.radioGroup }}
        value={selectedExportFormat}
        onChange={e => setSelectedExportFormat(e.target.value)}
      >
        <Radio
          value="2.0"
          checked={selectedExportFormat === '2.0'}
          containerClass={s.radioOption}
          removeOffset
          label={
            <Flexbox flexDirection="column">
              <Text variant="bodySm">
                {t('CredentialWallet.ShowPageHeader.OpenBadges20')}
              </Text>
              <Text variant="meta" color="grayDark">
                {t('CredentialWallet.ShowPageHeader.OpenBadges20Description')}
              </Text>
            </Flexbox>
          }
        />
        <Radio
          value="3.0"
          checked={selectedExportFormat === '3.0'}
          containerClass={s.radioOption}
          removeOffset
          label={
            <Flexbox flexDirection="column">
              <Text variant="bodySm">
                {t('CredentialWallet.ShowPageHeader.OpenBadges30')}
              </Text>
              <Text variant="meta" color="grayDark">
                {t('CredentialWallet.ShowPageHeader.OpenBadges30Description')}
              </Text>
            </Flexbox>
          }
        />
      </RadioGroup>

      <Flexbox justifyContent="space-between">
        <Button
          variant="text"
          color="secondary"
          onClick={() => setAnchor(null)}
          label={t('CredentialWallet.ShowPageHeader.Cancel')}
        />
        <Button
          variant="filled"
          onClick={() => {
            handleExport();
            setAnchor(null);
          }}
          label={t('CredentialWallet.ShowPageHeader.Export')}
        />
      </Flexbox>
    </Flexbox>
  );

  return (
    <Flexbox flexDirection="column">
      <Flexbox
        className={s.header}
        px={2}
        py={1.5}
        flexDirection="column"
        rowGap={1}
      >
        <Flexbox
          className={s.headerWrapper}
          justifyContent="space-between"
          width="100%"
        >
          <Flexbox className={s.third}>
            <Button
              href={credentialsListUrl}
              color="default"
              hasIconLeft
              icon="arrow"
              variant="text"
              label={t('CredentialWallet.ShowPageHeader.Back')}
              aria-label={t('CredentialWallet.ShowPageHeader.Back')}
            />
          </Flexbox>
          <Flexbox className={s.third} justifyContent="center">
            <Text variant="h4">{credentialName}</Text>
          </Flexbox>
          <Flexbox
            className={cn(s.third, s.buttons)}
            justifyContent="flex-end"
            alignItems="center"
            columnGap={1}
          >
            {!hideActions && (
              <>
                <Toggle
                  checked={isCredentialWalletPublic && isPublic}
                  disabled={!isCredentialWalletPublic}
                  label={t('CredentialWallet.ShowPageHeader.SetAsPublic')}
                  labelDirection="left"
                  onChange={(_, checked) => {
                    setCredentialVisibility({
                      type,
                      credentialId,
                      data: {
                        isPublic: checked,
                      },
                    });
                    setIsPublic(checked);
                  }}
                />

                <Tooltip title={getTooltip()}>
                  <div>
                    <Button
                      aria-label={t(
                        'CredentialWallet.ShowPageHeader.CopyToClipboard',
                      )}
                      disabled={!isPublic || !isCredentialWalletPublic}
                      hasIconLeft
                      hideLabel
                      icon={<FontAwesomeIcon icon={faLink} />}
                      onClick={copyToClipboard}
                      variant="filled"
                    />
                  </div>
                </Tooltip>
                {credentialCredentialType !== 'imported' ? (
                  <WithPopover
                    hideArrow
                    placement="bottom"
                    popoverContent={renderExportPopover}
                    paperClass={s.exportPopover}
                    anchorContent={() => (
                      <Tooltip
                        title={t('CredentialWallet.ShowPageHeader.ExportJSON')}
                      >
                        <div>
                          <Button
                            aria-label={t(
                              'CredentialWallet.ShowPageHeader.ExportCredential',
                            )}
                            hasIconLeft
                            icon={<FontAwesomeIcon icon={faDownload} />}
                            variant="filled"
                          />
                        </div>
                      </Tooltip>
                    )}
                  />
                ) : (
                  <Tooltip
                    title={t('CredentialWallet.ShowPageHeader.ExportJSON')}
                  >
                    <Button
                      aria-label={t(
                        'CredentialWallet.ShowPageHeader.ExportCredential',
                      )}
                      hasIconLeft
                      icon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={exportCredential}
                      variant="filled"
                    />
                  </Tooltip>
                )}
                <Tooltip
                  title={t(
                    'CredentialWallet.ShowPageHeader.StoreInOtherWallet',
                  )}
                >
                  <div>
                    <Button
                      aria-label={t(
                        'CredentialWallet.ShowPageHeader.ShareCredential',
                      )}
                      hasIconLeft
                      icon={<FontAwesomeIcon icon={faShare} />}
                      onClick={shareCredential}
                      variant="filled"
                    />
                  </div>
                </Tooltip>
              </>
            )}
          </Flexbox>
        </Flexbox>
      </Flexbox>
    </Flexbox>
  );
};

ShowPageHeader.propTypes = {
  credentialName: string.isRequired,
  isPublic: bool.isRequired,
  setIsPublic: func.isRequired,
  setCredentialVisibility: func.isRequired,
  type: string.isRequired,
  credentialId: number.isRequired,
  copyToClipboard: func.isRequired,
  exportCredential: func.isRequired,
  shareCredential: func.isRequired,
  hideActions: bool,
  credentialCredentialType: string.isRequired,
};

ShowPageHeader.defaultProps = {
  hideActions: false,
};

export default ShowPageHeader;
